<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { toast } from 'dashboard/helper/toast';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { useEditableAutomation } from 'dashboard/composables/useEditableAutomation';
import { AUTOMATION_ACTION_TYPES } from 'dashboard/routes/dashboard/settings/automation/constants';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import AutomationForm from 'dashboard/components-next/automation/AutomationForm.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const accountId = computed(() => getters.getCurrentAccountId.value);
const automationId = computed(() => Number(route.params.automationId));

const {
  automation,
  automationTypes,
  onEventChange,
  getConditionDropdownValues,
  appendNewAction,
  removeAction,
  resetAction,
  getActionDropdownValues,
  manifestCustomAttributes,
} = useAutomation();
const { formatAutomation } = useEditableAutomation();

const allCustomAttributes = ref([]);
const isSubmitting = ref(false);
const isLoading = ref(true);

const isFeatureEnabledonAccount = computed(
  () => getters['accounts/isFeatureEnabledonAccount'].value
);

const automationActionTypes = computed(() => {
  const slaEnabled = isFeatureEnabledonAccount.value(accountId.value, 'sla');
  return slaEnabled
    ? AUTOMATION_ACTION_TYPES
    : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');
});

const goToList = () => {
  router.replace({
    name: 'automation_list',
    params: { accountId: accountId.value },
  });
};

// Botão Voltar/Cancelar context-aware.
const goBack = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push({
      name: 'automation_list',
      params: { accountId: accountId.value },
    });
  }
};

const findAutomationInStore = () =>
  getters['automations/getAutomations'].value.find(
    record => record.id === automationId.value
  );

const loadAutomation = async () => {
  let record = findAutomationInStore();

  // Não achou no store (deep-link / refresh) — recarrega e tenta de novo.
  if (!record) {
    await store.dispatch('automations/get');
    record = findAutomationInStore();
  }

  // Nem após reload — sem fallback silencioso: avisa e volta pra lista.
  if (!record) {
    toast.error(t('AUTOMATION.EDIT.API.NOT_FOUND'));
    goToList();
    return;
  }

  manifestCustomAttributes();
  allCustomAttributes.value = getters['attributes/getAttributes'].value;

  automation.value = formatAutomation(
    record,
    allCustomAttributes.value,
    automationTypes,
    automationActionTypes.value
  );
  isLoading.value = false;
};

// As listas que o formatAutomation usa pra casar a regra salva com as opções
// (etiqueta por título, time/agente/caixa por id). Contatos e campanhas não
// entram nesse casamento: carregam em paralelo sem segurar o formulário.
const LISTS_FOR_MATCHING = ['inboxes', 'agents', 'teams', 'labels'];

onMounted(async () => {
  // Sem esperar as listas, abrir a URL direto mostrava a ação vazia e o botão
  // Atualizar desabilitado. Uma lista que falha NÃO trava a tela no spinner:
  // o formulário abre e o aviso diz qual lista faltou (a ação pode aparecer
  // vazia até recarregar).
  store.dispatch('contacts/get');
  store.dispatch('campaigns/get');
  const results = await Promise.allSettled(
    LISTS_FOR_MATCHING.map(list => store.dispatch(`${list}/get`))
  );
  const failed = LISTS_FOR_MATCHING.filter(
    (_, i) => results[i].status === 'rejected'
  );
  if (failed.length) {
    toast.error(
      t('AUTOMATION.EDIT.API.LISTS_FAILED', { lists: failed.join(', ') })
    );
  }
  await loadAutomation();
});

const submitAutomation = async payload => {
  isSubmitting.value = true;
  try {
    await store.dispatch('automations/update', payload);
    toast.success(t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE'));
    router.push({
      name: 'automation_list',
      params: { accountId: accountId.value },
    });
  } catch (error) {
    // 422 traz o motivo da recusa (ex.: ação de destino que falta); sem ele
    // o usuário só via "tente novamente mais tarde".
    toast.error(
      error?.response?.data?.message || t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <PageLayout body-padding="">
    <template #header>
      <PageHeader
        icon="i-lucide-workflow"
        accent="iris"
        :title="t('AUTOMATION.EDIT.TITLE')"
        :subtitle="t('AUTOMATION.DESCRIPTION')"
        back-route="automation_list"
      />
    </template>
    <woot-loading-state v-if="isLoading" :message="t('AUTOMATION.LOADING')" />
    <AutomationForm
      v-else-if="automation"
      v-model:automation="automation"
      :automation-types="automationTypes"
      mode="edit"
      :is-submitting="isSubmitting"
      :all-custom-attributes="allCustomAttributes"
      :on-event-change="onEventChange"
      :get-condition-dropdown-values="getConditionDropdownValues"
      :get-action-dropdown-values="getActionDropdownValues"
      :append-new-action="appendNewAction"
      :remove-action="removeAction"
      :reset-action="resetAction"
      @submit="submitAutomation"
      @cancel="goBack"
    />
  </PageLayout>
</template>
